import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserButton } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Field } from "@/components/kit";
import { LangToggle } from "@/components/layout/AppShell";
import { errMsg, validMf } from "@/lib/format";
import { Logo } from "@/components/Logo";

const FIELDS = ["name", "matricule_fiscal", "address", "city", "postal_code", "phone", "email", "activity"];

export default function Onboarding() {
  const { t, api, me, refreshMe, setCompanyId, creatingNew, setCreatingNew } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const [attach, setAttach] = useState(true);
  useEffect(() => {
    const slug = localStorage.getItem("mohassib_ref");
    if (slug) axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/demo/links/${slug}/public`).then((r) => setRef(r.data)).catch(() => localStorage.removeItem("mohassib_ref"));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!validMf(f.matricule_fiscal)) return toast.error("Matricule fiscal non conforme (ex. 1234567A/A/M/000)");
    setBusy(true);
    try {
      const { data } = await api.post("/companies", { ...f, demo_ref: ref && attach ? ref.slug : null });
      localStorage.removeItem("mohassib_ref");
      await refreshMe();
      setCompanyId(data.id);
      setCreatingNew(false);
      nav("/app", { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const accept = async (id) => {
    try {
      const { data } = await api.post(`/invitations/${id}/accept`);
      await refreshMe();
      setCompanyId(data.company_id);
      setCreatingNew(false);
      nav("/app", { replace: true });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const label = (k) => (k === "matricule_fiscal" ? t("mf") : k === "activity" ? "Activité" : t(k));
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
        <Logo size={34} />
        <div className="flex items-center gap-3">{creatingNew && <Button variant="ghost" onClick={() => setCreatingNew(false)} data-testid="onboarding-back">{t("cancel")}</Button>}<LangToggle /><UserButton /></div>
      </div>
      <div className="mx-auto max-w-3xl space-y-6 px-6 pb-16">
        {me?.invitations?.length > 0 && (
          <div className="rounded-xl border border-teal-200 bg-teal-50 p-5" data-testid="pending-invitations">
            <div className="eyebrow text-teal-800">{t("pending_invites")}</div>
            {me.invitations.map((i) => (
              <div key={i.id} className="mt-3 flex items-center justify-between gap-3">
                <span className="text-sm"><b>{i.company_name}</b> · {t(`r_${i.role}`)}</span>
                <Button size="sm" onClick={() => accept(i.id)} data-testid={`accept-invite-${i.id}`}>{t("join")}</Button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 fade-up">
          <div className="eyebrow">{t("company")}</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">{t("onboarding_title")}</h1>
          <p className="mt-2 text-sm text-slate-500">Essai gratuit activé automatiquement · {t("disclaimer")}</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {FIELDS.map((k) => (
              <Field key={k} label={label(k)} className={k === "name" || k === "address" ? "sm:col-span-2" : ""}>
                <Input data-testid={`onboarding-${k}`} required={k === "name" || k === "matricule_fiscal"} value={f[k] || ""}
                  placeholder={k === "matricule_fiscal" ? "1234567A/A/M/000" : ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              </Field>
            ))}
          </div>
          {ref && <label className="mt-5 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50/50 p-3 text-sm" data-testid="onboarding-firm-ref"><input type="checkbox" className="mt-0.5 accent-[#E30613]" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="onboarding-attach-firm" /><span>{t("demo_attach")} <b>{ref.firm_name}</b> {t("demo_attach_2")}</span></label>}
          <Button type="submit" disabled={busy} className="mt-6" data-testid="onboarding-submit">{t("create_company")}</Button>
        </form>
      </div>
    </div>
  );
}
